import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Strategy, Indicator, EntryRule, ExitRule, AssignedAccount } from '@/types/strategy';
import { SYMBOLS, TIMEFRAMES, INDICATOR_LABELS } from '@/types/strategy';
import { supabase } from '@/lib/supabase';
import AccountSelector from '@/pages/strategies/components/AccountSelector';
import StrategySignalMonitor from '@/pages/strategies/components/StrategySignalMonitor';
import TrailingStopMonitor from '@/pages/strategies/components/TrailingStopMonitor';
import ChinMonitor from '@/pages/strategies/components/ChinMonitor';
import AutoChinMonitor from '@/pages/strategies/components/AutoChinMonitor';

const INDICATOR_TYPES = ['SMA', 'BOLLINGER', 'HMA', 'TMA'] as const;

const CONDITIONS = [
  { value: 'crosses_above', label: 'Crosses Above' },
  { value: 'crosses_below', label: 'Crosses Below' },
  { value: 'greater_than', label: 'Greater Than' },
  { value: 'less_than', label: 'Less Than' },
  { value: 'equal_to', label: 'Equal To' },
];

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

function getDefaultIndicatorParams(type: Indicator['type']): Omit<Indicator, 'id'> {
  switch (type) {
    case 'BOLLINGER':
      return { type: 'BOLLINGER', period: 20, deviation: 2 };
    case 'SMA':
      return { type: 'SMA', period: 14 };
    case 'HMA':
      return { type: 'HMA', period: 14 };
    case 'TMA':
      return { type: 'TMA', period: 14 };
  }
}

function getDefaultEntryRule(indicatorId: string): Omit<EntryRule, 'id'> {
  return {
    indicatorId,
    condition: 'crosses_above',
    targetType: 'price',
    targetValue: '',
    logic: 'AND',
  };
}

function getDefaultExitRule(indicatorId: string): Omit<ExitRule, 'id'> {
  return {
    indicatorId,
    condition: 'crosses_below',
    targetType: 'price',
    targetValue: '',
    logic: 'AND',
  };
}

const EMPTY_STRATEGY: Omit<Strategy, 'id' | 'user_id' | 'created_at' | 'updated_at'> = {
  name: '',
  type: 'generic',
  symbol: 'EURUSD',
  symbol_list_id: null,
  timeframe: 'H1',
  description: '',
  indicators: [],
  entry_rules: [],
  exit_rules: [],
  risk_per_trade: 1.0,
  max_positions: 1,
  stop_loss_pips: 50,
  take_profit_pips: 100,
  mt4_copy_enabled: false,
  ctrader_copy_enabled: false,
  assigned_accounts: [],
  is_active: false,
  active_accounts: [],
  trailingStopParams: null,
  chinBotParams: null,
};

function mapDbToForm(dbRow: Record<string, unknown>): Strategy {
  const rawType = (dbRow.type as string) || 'generic';
  const normalizedType = rawType === 'trailing_stop' ? 'trailingStop' : rawType;
  return {
    id: String(dbRow.id || ''),
    user_id: String(dbRow.user_id || ''),
    name: String(dbRow.name || ''),
    type: normalizedType as Strategy['type'],
    symbol: String(dbRow.symbol || ''),
    symbol_list_id: (dbRow.symbol_list_id as string | null) || null,
    timeframe: String(dbRow.timeframe || ''),
    description: String(dbRow.description || ''),
    indicators: (dbRow.indicators as Indicator[]) || [],
    entry_rules: (dbRow.entry_rules as EntryRule[]) || [],
    exit_rules: (dbRow.exit_rules as ExitRule[]) || [],
    risk_per_trade: Number(dbRow.risk_per_trade ?? 1),
    max_positions: Number(dbRow.max_positions ?? 1),
    stop_loss_pips: Number(dbRow.stop_loss_pips ?? 50),
    take_profit_pips: Number(dbRow.take_profit_pips ?? 100),
    mt4_copy_enabled: Boolean(dbRow.mt4_copy_enabled),
    ctrader_copy_enabled: Boolean(dbRow.ctrader_copy_enabled),
    assigned_accounts: (dbRow.assigned_accounts as AssignedAccount[]) || [],
    is_active: Boolean(dbRow.is_active),
    active_accounts: (dbRow.active_accounts as string[]) || [],
    trailingStopParams: (dbRow.trailingstopparams as Strategy['trailingStopParams']) || null,
    chinBotParams: (dbRow.chinparams as Strategy['chinBotParams']) || null,
    created_at: String(dbRow.created_at || new Date().toISOString()),
    updated_at: String(dbRow.updated_at || new Date().toISOString()),
  };
}

export default function StrategyDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialAccountId = searchParams.get('accountId') || undefined;
  const strategiesBackUrl = initialAccountId ? `/strategies?accountId=${initialAccountId}` : '/strategies';
  const isNew = id === 'new';

  const [formData, setFormData] = useState({ ...EMPTY_STRATEGY });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [activeTab, setActiveTab] = useState<'config' | 'signals'>('config');
  const [ctraderAccounts, setCtraderAccounts] = useState<Array<{ id: number; account_id: string; account_name: string }>>([]);

  // Load cTrader accounts for monitor
  useEffect(() => {
    supabase.functions.invoke<{ connected: boolean; accounts: Array<{ id: number; account_id: string; account_name: string }> }>('ctrader-gateway', {
      method: 'POST',
      body: { action: 'status' },
    }).then(({ data }) => {
      if (data?.accounts) setCtraderAccounts(data.accounts);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    async function loadStrategy() {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('strategies')
          .select('*')
          .eq('id', id)
          .maybeSingle();
        if (error) throw error;
        if (data) {
          if (!cancelled) {
            const mapped = mapDbToForm(data);
            const { id: _id, user_id: _uid, created_at: _ca, updated_at: _ua, ...rest } = mapped;
            setFormData(rest);
          }
        } else {
          navigate(strategiesBackUrl);
        }
      } catch {
        if (!cancelled) navigate(strategiesBackUrl);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadStrategy();
    return () => { cancelled = true; };
  }, [id, isNew, navigate]);

  const updateField = useCallback((field: string, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const addIndicator = useCallback((type: Indicator['type']) => {
    setFormData((prev) => ({
      ...prev,
      indicators: [...prev.indicators, { id: generateId(), ...getDefaultIndicatorParams(type) }],
    }));
  }, []);

  const removeIndicator = useCallback((indicatorId: string) => {
    setFormData((prev) => ({
      ...prev,
      indicators: prev.indicators.filter((ind) => ind.id !== indicatorId),
      entry_rules: prev.entry_rules.filter((r) => r.indicatorId !== indicatorId),
      exit_rules: prev.exit_rules.filter((r) => r.indicatorId !== indicatorId),
    }));
  }, []);

  const updateIndicator = useCallback((indicatorId: string, field: string, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      indicators: prev.indicators.map((ind) => ind.id === indicatorId ? { ...ind, [field]: value } : ind),
    }));
  }, []);

  const addEntryRule = useCallback((indicatorId: string) => {
    setFormData((prev) => ({
      ...prev,
      entry_rules: [...prev.entry_rules, { id: generateId(), ...getDefaultEntryRule(indicatorId) }],
    }));
  }, []);

  const removeEntryRule = useCallback((ruleId: string) => {
    setFormData((prev) => ({ ...prev, entry_rules: prev.entry_rules.filter((r) => r.id !== ruleId) }));
  }, []);

  const updateEntryRule = useCallback((ruleId: string, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      entry_rules: prev.entry_rules.map((r) => r.id === ruleId ? { ...r, [field]: value } : r),
    }));
  }, []);

  const addExitRule = useCallback((indicatorId: string) => {
    setFormData((prev) => ({
      ...prev,
      exit_rules: [...prev.exit_rules, { id: generateId(), ...getDefaultExitRule(indicatorId) }],
    }));
  }, []);

  const removeExitRule = useCallback((ruleId: string) => {
    setFormData((prev) => ({ ...prev, exit_rules: prev.exit_rules.filter((r) => r.id !== ruleId) }));
  }, []);

  const updateExitRule = useCallback((ruleId: string, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      exit_rules: prev.exit_rules.map((r) => r.id === ruleId ? { ...r, [field]: value } : r),
    }));
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Strategy name is required';
    if (!formData.symbol) newErrors.symbol = 'Symbol is required';
    if (formData.indicators.length === 0) newErrors.indicators = 'Add at least one indicator';
    if (formData.entry_rules.length === 0) newErrors.entry_rules = 'Add at least one entry rule';
    if (formData.exit_rules.length === 0) newErrors.exit_rules = 'Add at least one exit rule';
    if (formData.risk_per_trade <= 0 || formData.risk_per_trade > 100) newErrors.risk_per_trade = 'Risk must be between 0.1% and 100%';
    if (formData.max_positions < 1 || formData.max_positions > 50) newErrors.max_positions = 'Max positions must be between 1 and 50';
    if (formData.stop_loss_pips <= 0) newErrors.stop_loss_pips = 'Stop loss must be greater than 0';
    if (formData.take_profit_pips <= 0) newErrors.take_profit_pips = 'Take profit must be greater than 0';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSave = useCallback(async () => {
    if (!validate()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const payload = {
        name: formData.name, symbol: formData.symbol, timeframe: formData.timeframe,
        description: formData.description, indicators: formData.indicators,
        entry_rules: formData.entry_rules, exit_rules: formData.exit_rules,
        risk_per_trade: formData.risk_per_trade, max_positions: formData.max_positions,
        stop_loss_pips: formData.stop_loss_pips, take_profit_pips: formData.take_profit_pips,
        mt4_copy_enabled: formData.mt4_copy_enabled, ctrader_copy_enabled: formData.ctrader_copy_enabled,
        assigned_accounts: formData.assigned_accounts, is_active: formData.is_active,
        updated_at: new Date().toISOString(),
      };
      if (isNew) {
        const { error } = await supabase.from('strategies').insert({ ...payload, id: crypto.randomUUID(), user_id: user?.id || null, created_at: new Date().toISOString() });
        if (error) throw error;
      } else {
        const { error } = await supabase.from('strategies').update(payload).eq('id', id);
        if (error) throw error;
      }
      setSaving(false);
      navigate(strategiesBackUrl);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save strategy');
      setSaving(false);
    }
  }, [formData, isNew, id, validate, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-foreground-500">Loading strategy...</p>
        </div>
      </div>
    );
  }

  const strategyForMonitor: Strategy = {
    ...formData,
    id: id || '',
    user_id: '',
    created_at: '',
    updated_at: '',
  };

  // Compute active state for TrailingStop monitor
  const isTrailingActive = initialAccountId && formData.active_accounts
    ? formData.active_accounts.includes(initialAccountId)
    : formData.is_active;

  // Bot monitor types render a dedicated control panel instead of the generic config form.
  const isMonitorType = strategyForMonitor.type === 'trailingStop'
    || strategyForMonitor.type === 'chin'
    || strategyForMonitor.type === 'autochinbot';

  return (
    <div className="px-4 md:px-6 py-4 md:py-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(strategiesBackUrl)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div>
            <h1 className="text-lg md:text-xl font-heading font-bold text-foreground-950">
              {isNew ? 'Create Strategy' : isMonitorType ? 'Monitor' : 'Edit Strategy'}
            </h1>
            <p className="text-foreground-600 text-xs md:text-sm mt-0.5">
              {isNew ? 'Configure a new automated trading strategy' : isMonitorType ? formData.name : `Editing: ${formData.name}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isNew && !isMonitorType && (
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-foreground-600">Active</span>
              <button
                type="button"
                onClick={() => updateField('is_active', !formData.is_active)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 cursor-pointer ${formData.is_active ? 'bg-emerald-500' : 'bg-background-300'}`}
              >
                <span
                  className={`inline-block rounded-full bg-background-50 transition-transform duration-200 ${formData.is_active ? 'translate-x-[22px]' : 'translate-x-[3px]'}`}
                  style={{ width: '18px', height: '18px' }}
                ></span>
              </button>
            </label>
          )}
          {!isMonitorType && (
            <>
              <button
                onClick={() => navigate(strategiesBackUrl)}
                className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {saving ? (
                  <span className="flex items-center gap-1.5">
                    <div className="w-4 h-4 border-2 border-background-50 border-t-transparent rounded-full animate-spin"></div>
                    Saving...
                  </span>
                ) : isNew ? 'Create Strategy' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Save error */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Failed to save</p>
            <p className="text-xs text-red-600 mt-0.5">{saveError}</p>
          </div>
          <button onClick={() => setSaveError(null)} className="w-6 h-6 flex items-center justify-center rounded-md text-red-400 hover:text-red-600 transition-all cursor-pointer">
            <i className="ri-close-line text-sm"></i>
          </button>
        </div>
      )}

      {/* Tabs — only for non-TrailingStop existing strategies */}
      {!isNew && !isMonitorType && (
        <div className="flex items-center gap-1 mb-6 bg-background-100 border border-background-200/70 rounded-xl p-1 w-fit">
          <button
            type="button"
            onClick={() => setActiveTab('config')}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === 'config' ? 'bg-background-50 text-foreground-900' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            <i className="ri-settings-3-line mr-1"></i>
            Configuration
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('signals')}
            className={`px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === 'signals' ? 'bg-accent-500 text-background-50' : 'text-foreground-500 hover:text-foreground-700'}`}
          >
            <i className="ri-radar-line mr-1"></i>
            Live Signals
          </button>
        </div>
      )}

      {!isNew && strategyForMonitor.type === 'chin' && (
        <ChinMonitor
          strategy={strategyForMonitor}
          ctraderAccounts={ctraderAccounts}
          isActive={isTrailingActive}
          initialAccountId={initialAccountId}
        />
      )}

      {/* AutoChinBot: central control monitor */}
      {!isNew && strategyForMonitor.type === 'autochinbot' && (
        <AutoChinMonitor
          strategy={strategyForMonitor}
          ctraderAccounts={ctraderAccounts}
          isActive={isTrailingActive}
          initialAccountId={initialAccountId}
        />
      )}

      {/* TrailingStop: Full-width monitor */}
      {!isNew && strategyForMonitor.type === 'trailingStop' && (
        <TrailingStopMonitor
          strategy={strategyForMonitor}
          ctraderAccounts={ctraderAccounts}
          isActive={isTrailingActive}
          initialAccountId={initialAccountId}
        />
      )}

      {/* Live Signals Tab (non-TrailingStop only) */}
      {activeTab === 'signals' && !isNew && !isMonitorType && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-3">
            <StrategySignalMonitor strategy={strategyForMonitor} />
          </div>
        </div>
      )}

      {/* Config Tab (non-TrailingStop only) */}
      {(activeTab === 'config' || isNew) && !isMonitorType && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
                  <i className="ri-information-line text-primary-400 text-sm"></i>
                </div>
                <h2 className="text-sm font-heading font-semibold text-foreground-900">Basic Information</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                    Strategy Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="e.g. Alert Bot v1"
                    className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${errors.name ? 'border-red-400 focus:ring-red-400/20' : 'border-background-300/60 focus:border-primary-500/50 focus:ring-primary-500/10'}`}
                  />
                  {errors.name && <p className="text-red-400 text-[10px] mt-1">{errors.name}</p>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Symbol <span className="text-red-400">*</span></label>
                    <select
                      value={formData.symbol}
                      onChange={(e) => updateField('symbol', e.target.value)}
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/10 transition-all cursor-pointer"
                    >
                      {SYMBOLS.map((sym) => <option key={sym} value={sym}>{sym}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Timeframe <span className="text-red-400">*</span></label>
                    <select
                      value={formData.timeframe}
                      onChange={(e) => updateField('timeframe', e.target.value)}
                      className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/10 transition-all cursor-pointer"
                    >
                      {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Describe your strategy logic..."
                    rows={3}
                    className="w-full px-3 py-2.5 bg-background-50 border border-background-300/60 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 focus:ring-2 focus:ring-primary-500/10 transition-all resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Indicators */}
            <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center">
                    <i className="ri-line-chart-line text-accent-400 text-sm"></i>
                  </div>
                  <h2 className="text-sm font-heading font-semibold text-foreground-900">Indicators <span className="text-red-400">*</span></h2>
                </div>
                <div className="relative group">
                  <button type="button" className="flex items-center gap-1.5 px-3 py-2 bg-accent-500 text-background-50 rounded-lg text-xs font-medium hover:bg-accent-600 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line text-sm"></i>
                    <span>Add Indicator</span>
                  </button>
                  <div className="absolute right-0 top-full mt-1 w-48 bg-background-50 border border-background-200/70 rounded-xl py-1 z-20 hidden group-hover:block">
                    {INDICATOR_TYPES.map((type) => (
                      <button key={type} type="button" onClick={() => addIndicator(type)} className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-all cursor-pointer flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent-500"></span>
                        {INDICATOR_LABELS[type]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {errors.indicators && <p className="text-red-400 text-[10px] mb-3">{errors.indicators}</p>}

              {formData.indicators.length === 0 ? (
                <div className="bg-background-50 rounded-lg border border-dashed border-background-300/60 p-6 text-center">
                  <p className="text-foreground-500 text-xs mb-3">No indicators added yet</p>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {INDICATOR_TYPES.map((type) => (
                      <button key={type} type="button" onClick={() => addIndicator(type)} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-background-200/70 text-foreground-600 hover:bg-background-300/60 transition-all cursor-pointer whitespace-nowrap">
                        + {type}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.indicators.map((ind, idx) => (
                    <div key={ind.id} className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-md bg-accent-500/10 flex items-center justify-center text-[10px] font-bold text-accent-400">{idx + 1}</span>
                          <span className="text-sm font-semibold text-foreground-800">{INDICATOR_LABELS[ind.type]}</span>
                        </div>
                        <button type="button" onClick={() => removeIndicator(ind.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-400 hover:bg-red-50 transition-all cursor-pointer">
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[10px] font-medium text-foreground-500 mb-1">Period</label>
                          <input
                            type="number"
                            value={ind.period}
                            onChange={(e) => updateIndicator(ind.id, 'period', parseInt(e.target.value) || 1)}
                            min={1} max={500}
                            className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/10 transition-all"
                          />
                        </div>
                        {ind.type === 'BOLLINGER' && (
                          <div>
                            <label className="block text-[10px] font-medium text-foreground-500 mb-1">Deviation</label>
                            <input
                              type="number"
                              value={ind.deviation || 2}
                              onChange={(e) => updateIndicator(ind.id, 'deviation', parseFloat(e.target.value) || 0.1)}
                              min={0.1} max={10} step={0.1}
                              className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-accent-500/50 focus:ring-2 focus:ring-accent-500/10 transition-all"
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-200/40">
                        <button type="button" onClick={() => addEntryRule(ind.id)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-all cursor-pointer whitespace-nowrap">
                          <i className="ri-login-box-line text-xs"></i> Add Entry Rule
                        </button>
                        <button type="button" onClick={() => addExitRule(ind.id)} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all cursor-pointer whitespace-nowrap">
                          <i className="ri-logout-box-line text-xs"></i> Add Exit Rule
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Entry Rules */}
            {formData.entry_rules.length > 0 && (
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <i className="ri-login-box-line text-emerald-400 text-sm"></i>
                  </div>
                  <h2 className="text-sm font-heading font-semibold text-foreground-900">Entry Rules</h2>
                </div>
                {errors.entry_rules && <p className="text-red-400 text-[10px] mb-3">{errors.entry_rules}</p>}
                <div className="space-y-4">
                  {formData.entry_rules.map((rule, idx) => {
                    
                    return (
                      <div key={rule.id} className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5">
                            <i className="ri-login-box-line"></i>
                            Entry Rule {idx + 1}
                            {idx > 0 && rule.logic && <span className="text-foreground-400 font-normal">— {rule.logic}</span>}
                          </span>
                          <button type="button" onClick={() => removeEntryRule(rule.id)} className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-400 transition-all cursor-pointer">
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[10px] font-medium text-foreground-500 mb-1">Indicator</label>
                            <select value={rule.indicatorId} onChange={(e) => updateEntryRule(rule.id, 'indicatorId', e.target.value)} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/10 transition-all cursor-pointer">
                              {formData.indicators.map((ind) => <option key={ind.id} value={ind.id}>{ind.type}({ind.period})</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-foreground-500 mb-1">Condition</label>
                            <select value={rule.condition} onChange={(e) => updateEntryRule(rule.id, 'condition', e.target.value)} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/10 transition-all cursor-pointer">
                              {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-foreground-500 mb-1">Target</label>
                            <div className="flex items-center gap-2">
                              <select value={rule.targetType} onChange={(e) => updateEntryRule(rule.id, 'targetType', e.target.value)} className="flex-1 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/10 transition-all cursor-pointer">
                                <option value="price">Price</option>
                                <option value="level">Fixed Level</option>
                                <option value="indicator">Indicator</option>
                              </select>
                              {rule.targetType === 'level' && (
                                <input type="number" value={rule.targetValue} onChange={(e) => updateEntryRule(rule.id, 'targetValue', e.target.value)} placeholder="Level" className="w-20 px-2 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none transition-all" />
                              )}
                              {rule.targetType === 'indicator' && (
                                <select value={rule.targetValue} onChange={(e) => updateEntryRule(rule.id, 'targetValue', e.target.value)} className="w-28 px-2 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none transition-all cursor-pointer">
                                  {formData.indicators.map((ind) => <option key={ind.id} value={ind.id}>{ind.type}({ind.period})</option>)}
                                </select>
                              )}
                            </div>
                          </div>
                        </div>
                        {idx > 0 && (
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-200/40">
                            <label className="text-[10px] font-medium text-foreground-500">Combine with:</label>
                            <select value={rule.logic || 'AND'} onChange={(e) => updateEntryRule(rule.id, 'logic', e.target.value)} className="px-2 py-1 bg-background-100 border border-background-200/70 rounded-md text-xs text-foreground-900 focus:outline-none cursor-pointer">
                              <option value="AND">AND</option>
                              <option value="OR">OR</option>
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Exit Rules */}
            {formData.exit_rules.length > 0 && (
              <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <i className="ri-logout-box-line text-red-400 text-sm"></i>
                  </div>
                  <h2 className="text-sm font-heading font-semibold text-foreground-900">Exit Rules</h2>
                </div>
                {errors.exit_rules && <p className="text-red-400 text-[10px] mb-3">{errors.exit_rules}</p>}
                <div className="space-y-4">
                  {formData.exit_rules.map((rule, idx) => {
                    
                    return (
                      <div key={rule.id} className="bg-background-50 rounded-lg border border-background-200/70 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-semibold text-red-400 flex items-center gap-1.5">
                            <i className="ri-logout-box-line"></i>
                            Exit Rule {idx + 1}
                            {idx > 0 && rule.logic && <span className="text-foreground-400 font-normal">— {rule.logic}</span>}
                          </span>
                          <button type="button" onClick={() => removeExitRule(rule.id)} className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-400 transition-all cursor-pointer">
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-[10px] font-medium text-foreground-500 mb-1">Indicator</label>
                            <select value={rule.indicatorId} onChange={(e) => updateExitRule(rule.id, 'indicatorId', e.target.value)} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/10 transition-all cursor-pointer">
                              {formData.indicators.map((ind) => <option key={ind.id} value={ind.id}>{ind.type}({ind.period})</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-foreground-500 mb-1">Condition</label>
                            <select value={rule.condition} onChange={(e) => updateExitRule(rule.id, 'condition', e.target.value)} className="w-full px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/10 transition-all cursor-pointer">
                              {CONDITIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                          </div>
                          <div>
                            <label className="block text-[10px] font-medium text-foreground-500 mb-1">Target</label>
                            <div className="flex items-center gap-2">
                              <select value={rule.targetType} onChange={(e) => updateExitRule(rule.id, 'targetType', e.target.value)} className="flex-1 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-red-500/50 focus:ring-2 focus:ring-red-500/10 transition-all cursor-pointer">
                                <option value="price">Price</option>
                                <option value="level">Fixed Level</option>
                                <option value="indicator">Indicator</option>
                              </select>
                              {rule.targetType === 'level' && (
                                <input type="number" value={rule.targetValue} onChange={(e) => updateExitRule(rule.id, 'targetValue', e.target.value)} placeholder="Level" className="w-20 px-2 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none transition-all" />
                              )}
                              {rule.targetType === 'indicator' && (
                                <select value={rule.targetValue} onChange={(e) => updateExitRule(rule.id, 'targetValue', e.target.value)} className="w-28 px-2 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none transition-all cursor-pointer">
                                  {formData.indicators.map((ind) => <option key={ind.id} value={ind.id}>{ind.type}({ind.period})</option>)}
                                </select>
                              )}
                            </div>
                          </div>
                        </div>
                        {idx > 0 && (
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-background-200/40">
                            <label className="text-[10px] font-medium text-foreground-500">Combine with:</label>
                            <select value={rule.logic || 'AND'} onChange={(e) => updateExitRule(rule.id, 'logic', e.target.value)} className="px-2 py-1 bg-background-100 border border-background-200/70 rounded-md text-xs text-foreground-900 focus:outline-none cursor-pointer">
                              <option value="AND">AND</option>
                              <option value="OR">OR</option>
                            </select>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Risk & Settings */}
          <div className="space-y-6">
            {/* Risk Management */}
            <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-secondary-500/10 flex items-center justify-center">
                  <i className="ri-shield-line text-secondary-400 text-sm"></i>
                </div>
                <h2 className="text-sm font-heading font-semibold text-foreground-900">Risk Management</h2>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Risk Per Trade (%)</label>
                  <div className="relative">
                    <input type="number" value={formData.risk_per_trade} onChange={(e) => updateField('risk_per_trade', parseFloat(e.target.value) || 0)} min={0.1} max={100} step={0.1} className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all ${errors.risk_per_trade ? 'border-red-400 focus:ring-red-400/20' : 'border-background-300/60 focus:border-secondary-500/50 focus:ring-secondary-500/10'}`} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm">%</span>
                  </div>
                  {errors.risk_per_trade && <p className="text-red-400 text-[10px] mt-1">{errors.risk_per_trade}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Max Positions</label>
                  <input type="number" value={formData.max_positions} onChange={(e) => updateField('max_positions', parseInt(e.target.value) || 1)} min={1} max={50} className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all ${errors.max_positions ? 'border-red-400 focus:ring-red-400/20' : 'border-background-300/60 focus:border-secondary-500/50 focus:ring-secondary-500/10'}`} />
                  {errors.max_positions && <p className="text-red-400 text-[10px] mt-1">{errors.max_positions}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Stop Loss (pips)</label>
                  <div className="relative">
                    <input type="number" value={formData.stop_loss_pips} onChange={(e) => updateField('stop_loss_pips', parseFloat(e.target.value) || 0)} min={1} step={1} className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all ${errors.stop_loss_pips ? 'border-red-400 focus:ring-red-400/20' : 'border-background-300/60 focus:border-secondary-500/50 focus:ring-secondary-500/10'}`} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm">pips</span>
                  </div>
                  {errors.stop_loss_pips && <p className="text-red-400 text-[10px] mt-1">{errors.stop_loss_pips}</p>}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Take Profit (pips)</label>
                  <div className="relative">
                    <input type="number" value={formData.take_profit_pips} onChange={(e) => updateField('take_profit_pips', parseFloat(e.target.value) || 0)} min={1} step={1} className={`w-full px-3 py-2.5 bg-background-50 border rounded-lg text-sm text-foreground-900 focus:outline-none focus:ring-2 transition-all ${errors.take_profit_pips ? 'border-red-400 focus:ring-red-400/20' : 'border-background-300/60 focus:border-secondary-500/50 focus:ring-secondary-500/10'}`} />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm">pips</span>
                  </div>
                  {errors.take_profit_pips && <p className="text-red-400 text-[10px] mt-1">{errors.take_profit_pips}</p>}
                </div>
                <div className="bg-background-50 rounded-lg p-3 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-foreground-500">Risk/Reward Ratio</span>
                    <span className="text-xs font-mono font-bold text-foreground-800">1:{formData.stop_loss_pips > 0 ? (formData.take_profit_pips / formData.stop_loss_pips).toFixed(1) : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-foreground-500">Max Total Risk</span>
                    <span className="text-xs font-mono font-bold text-foreground-800">{(formData.risk_per_trade * formData.max_positions).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Copy Trade Account Assignment */}
            <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-accent-500/10 flex items-center justify-center">
                  <i className="ri-link text-accent-400 text-sm"></i>
                </div>
                <h2 className="text-sm font-heading font-semibold text-foreground-900">Copy Trade Accounts</h2>
              </div>
              <AccountSelector
                selected={formData.assigned_accounts}
                onChange={(accounts) => {
                  setFormData((prev) => ({
                    ...prev,
                    assigned_accounts: accounts,
                    mt4_copy_enabled: accounts.some((a) => a.platform === 'mt4'),
                    ctrader_copy_enabled: accounts.some((a) => a.platform === 'ctrader'),
                  }));
                }}
              />
            </div>

            {/* Summary */}
            <div className="bg-background-100 rounded-xl border border-background-200/70 p-5 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-background-200/70 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-foreground-500 text-sm"></i>
                </div>
                <h2 className="text-sm font-heading font-semibold text-foreground-900">Summary</h2>
              </div>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">Indicators</span>
                  <span className="font-mono font-medium text-foreground-800">{formData.indicators.length}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">Entry Rules</span>
                  <span className="font-mono font-medium text-foreground-800">{formData.entry_rules.length}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-foreground-500">Exit Rules</span>
                  <span className="font-mono font-medium text-foreground-800">{formData.exit_rules.length}</span>
                </div>
                <div className="flex items-center justify-between text-xs border-t border-background-200/60 pt-2.5 mt-2.5">
                  <span className="text-foreground-500">Status</span>
                  <span className={`font-medium ${formData.is_active ? 'text-emerald-400' : 'text-foreground-400'}`}>
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}